import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, Zap, Shield, Flame, Gauge, Target } from 'lucide-react';

const ProductDetail = ({ product, onBack }) => {
  const [activeImage, setActiveImage] = React.useState(product?.image);

  React.useEffect(() => {
    if (product) setActiveImage(product.image);
  }, [product]);

  if (!product) return null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-32 pb-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8 lg:sticky lg:top-32"
          >
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-primary font-bold hover:translate-x-[-4px] transition-transform w-fit"
            >
              <ArrowLeft size={20} />
              BACK TO HOME
            </button>
            <div className="rounded-[60px] overflow-hidden glass border border-white/10 aspect-video relative group">
              <img
                key={activeImage}
                src={activeImage || product.image}
                alt={product.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            </div>

            <div className="grid grid-cols-3 gap-6">
              {(product.gallery || [product.image, product.image, product.image]).map((imgSrc, i) => (
                <div 
                  key={i} 
                  onClick={() => setActiveImage(imgSrc)}
                  className={`aspect-square rounded-3xl glass border overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-300 ${activeImage === imgSrc ? 'opacity-100 border-primary shadow-[0_0_15px_rgba(45,90,39,0.3)]' : 'border-white/5 opacity-60 hover:opacity-100'}`}
                >
                  <img src={imgSrc} alt={`${product.title} variant ${i+1}`} className={`w-full h-full object-cover transition-all duration-500 ${activeImage === imgSrc ? 'grayscale-0' : 'grayscale hover:grayscale-0'}`} />
                </div>
              ))}
            </div>
          </motion.div>

          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-12 text-left"
          >
            <div>
              <span className={`px-4 py-1.5 rounded-full bg-${product.color}/10 text-${product.color} text-xs font-black tracking-widest uppercase mb-6 inline-block border border-${product.color}/20`}>
                Premium Grade Product
              </span>
              <h1 className="text-6xl md:text-8xl font-black syne tracking-tighter leading-none mb-8">
                {product.title}
              </h1>
              <p className="text-xl text-slate-400 font-medium leading-relaxed">
                {product.desc}
              </p>

              {product.title === "Bio Mass Pellets" && (
                <div className="mt-8 grid sm:grid-cols-2 gap-6">
                  <div className={`p-6 rounded-3xl border border-${product.color}/20 bg-${product.color}/5`}>
                    <h4 className={`text-${product.color} font-bold tracking-widest uppercase text-xs mb-4`}>Available Sizes</h4>
                    <div className="flex flex-wrap gap-3 text-white font-black syne">
                      <span className="px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 shadow-lg">6mm</span>
                      <span className="px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 shadow-lg">8mm</span>
                    </div>
                  </div>
                  <div className={`p-6 rounded-3xl border border-${product.color}/20 bg-${product.color}/5`}>
                    <h4 className={`text-${product.color} font-bold tracking-widest uppercase text-xs mb-4`}>Material Types</h4>
                    <div className="flex flex-col gap-4 text-white font-bold text-sm">
                      <span className="flex items-center gap-4">
                        <div className={`w-2 h-2 rounded-full bg-${product.color} shadow-[0_0_10px_currentColor]`}></div>
                        Agro Waste Pellets
                      </span>
                      <span className="flex items-center gap-4">
                        <div className={`w-2 h-2 rounded-full bg-${product.color} shadow-[0_0_10px_currentColor]`}></div>
                        Saw Dust Pellets
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {product.title.includes("Coal") && (
                <div className="mt-8 grid sm:grid-cols-2 gap-6">
                  <div className={`p-6 rounded-3xl border border-${product.color}/20 bg-${product.color}/5`}>
                    <h4 className={`text-${product.color} font-bold tracking-widest uppercase text-xs mb-4`}>Available Sizes</h4>
                    <div className="flex flex-wrap gap-3 text-white font-black syne">
                      <span className="px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 shadow-lg">90mm</span>
                    </div>
                  </div>
                  <div className={`p-6 rounded-3xl border border-${product.color}/20 bg-${product.color}/5`}>
                    <h4 className={`text-${product.color} font-bold tracking-widest uppercase text-xs mb-4`}>Material Types</h4>
                    <div className="flex flex-col gap-4 text-white font-bold text-sm">
                      <span className="flex items-center gap-4">
                        <div className={`w-2 h-2 rounded-full bg-${product.color} shadow-[0_0_10px_currentColor]`}></div>
                        Agro Waste Bio Coal
                      </span>
                      <span className="flex items-center gap-4">
                        <div className={`w-2 h-2 rounded-full bg-${product.color} shadow-[0_0_10px_currentColor]`}></div>
                        Saw Dust Bio Coal
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-8">
              {product.features.map((feat, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl bg-${product.color}/10 flex items-center justify-center text-${product.color}`}>
                    <CheckCircle size={20} />
                  </div>
                  <span className="text-lg font-bold tracking-tight text-white">{feat}</span>
                </div>
              ))}
            </div>

            <div className="p-10 rounded-[40px] glass border border-white/10 relative overflow-hidden group">
              <div className={`absolute top-0 right-0 w-32 h-32 bg-${product.color}/10 blur-[60px] -z-10`}></div>
              <h3 className="text-2xl font-black syne mb-8 tracking-tight">Technical Specifications</h3>
              <div className="space-y-6">
                {[
                  { label: "Calorific Value", value: product.title.includes('Coal') ? "3400 to 4400 kcal/kg" : "3800 to 4800 kcal/kg", icon: Flame },
                  { label: "Moisture Content", value: "< 8%", icon: Gauge },
                  { label: "Ash Content", value: "< 1.5%", icon: Target },
                  { label: "Carbon Efficiency", value: "99.9%", icon: Zap },
                ].map((spec, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-white/5 pb-4">
                    <div className="flex items-center gap-3 text-slate-400">
                      <spec.icon size={18} />
                      <span className="font-bold text-sm uppercase tracking-wider">{spec.label}</span>
                    </div>
                    <span className="text-white font-black syne">{spec.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-6 pt-8">
              <button className="px-12 py-6 bg-primary hover:bg-primary-dark text-white rounded-full font-black syne tracking-widest text-sm transition-all shadow-2xl hover:scale-105 flex items-center gap-3">
                REQUEST PRICE QUOTE
                <Zap size={18} fill="currentColor" />
              </button>
              <button className="px-12 py-6 glass text-white hover:bg-white/10 rounded-full font-black syne tracking-widest text-sm transition-all flex items-center gap-3">
                <Shield size={18} />
                CERTIFICATIONS
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
